import {
  ADND_COVERAGE_TYPES,
  PEPM_SUPPORTED_COVERAGE_TYPES,
  RATE_STRUCTURES,
  STANDARD_PEPM_COVERAGE_TYPES,
} from "../constants";
import { RateScheduleFormData } from "../types/models";

interface RateStructureFlags {
  isAgeBanded: boolean;
  isTiered: boolean;
  isPepm: boolean;
  isFlat: boolean;
  isCriticalIllness: boolean;
  showAgeBandConfig: boolean;
  showTierConfig: boolean;
  showStructureDetail: boolean;
  // Tiered/PEPM render their own AD&D section directly in RatesTab; Flat and
  // Age Banded already render one internally (see FlatRateConfigSection /
  // AgeBandedRateSection), so this only ever gates the Tiered/PEPM case.
  showAdndConfig: boolean;
}

export const isCriticalIllnessCoverageType = (coverageType?: string): boolean =>
  coverageType?.toLowerCase() === "criticalillness";

export const getRateStructureFlags = (
  formData: RateScheduleFormData,
  coverageType?: string,
): RateStructureFlags => {
  const isAgeBanded = formData.rateStructure === "ageBanded";
  const isTiered = formData.rateStructure === "tiered";
  const isPepm = formData.rateStructure === "pepm";
  const isFlat = formData.rateStructure === "flat";
  const isCriticalIllness = isCriticalIllnessCoverageType(coverageType);
  const showAgeBandConfig =
    isAgeBanded || formData.structureDetail === "tiered";
  const showTierConfig =
    isTiered || (isAgeBanded && formData.structureDetail === "tiered");
  const showStructureDetail = isAgeBanded || isFlat || isPepm;
  const showAdndConfig =
    (isTiered || isPepm) && isLifeCoverageType(coverageType);

  return {
    isAgeBanded,
    isTiered,
    isPepm,
    isFlat,
    isCriticalIllness,
    showAgeBandConfig,
    showTierConfig,
    showStructureDetail,
    showAdndConfig,
  };
};

export const getAvailableRateStructures = (
  coverageType?: string,
): (typeof RATE_STRUCTURES)[number][] =>
  RATE_STRUCTURES.filter((structure) => {
    if (structure.value === "pepm") {
      return PEPM_SUPPORTED_COVERAGE_TYPES.includes(
        coverageType?.toLowerCase() as (typeof PEPM_SUPPORTED_COVERAGE_TYPES)[number],
      );
    }
    return true;
  });

// Map covered benefit type + rate per to backend unit enum
export const mapToBackendUnit = (
  coveredBenefitType: string,
  ratePer: string,
): string => {
  if (coveredBenefitType === "coveredPayroll") {
    switch (ratePer) {
      case "per10":
        return "per10OfSalary";
      case "per100":
        return "per100OfSalary";
      case "per1000":
        return "per1000OfSalary";
      default:
        return "per100OfSalary";
    }
  }
  // coveredBenefit or enrolledElectionVolume use volume units
  switch (ratePer) {
    case "per10":
      return "volumePer10";
    case "per100":
      return "volumePer100";
    case "per1000":
      return "volumePer1000";
    default:
      return "volumePer1000";
  }
};

/** Check if coverage type is a life product that supports AD&D */
export const isLifeCoverageType = (coverageType?: string): boolean =>
  ADND_COVERAGE_TYPES.includes(
    coverageType?.toLowerCase() as (typeof ADND_COVERAGE_TYPES)[number],
  );

/** Check if coverage type is Basic Life specifically (not Voluntary Life). */
export const isBasicLifeCoverageType = (coverageType?: string): boolean =>
  coverageType?.toLowerCase() === "basiclifeadnd";

export const isStandardPepmCoverageType = (coverageType: string): boolean =>
  STANDARD_PEPM_COVERAGE_TYPES.includes(
    coverageType.toLowerCase() as (typeof STANDARD_PEPM_COVERAGE_TYPES)[number],
  );
